import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

// On native iOS/Android, web navigator.geolocation inside the WKWebView shows a
// second WebKit prompt ("localhost would like to use your location") on top of
// the OS permission prompt. The Capacitor plugin talks to CoreLocation /
// FusedLocation directly, so users only ever see the single OS prompt.
// On the web (dev server, browsers) this falls back to navigator.geolocation.

type Success = (position: GeolocationPosition) => void;
type Failure = (error: any) => void;

const isNative = () => Capacitor.isNativePlatform();

const nativeWatches = new Map<number, Promise<string>>();
let nextWatchId = 1;

export const appGeolocation = {
  getCurrentPosition(success: Success, error?: Failure, options?: PositionOptions) {
    if (!isNative()) {
      if (!navigator.geolocation) { error?.(new Error('Geolocation unavailable')); return; }
      navigator.geolocation.getCurrentPosition(success, error, options);
      return;
    }
    Geolocation.getCurrentPosition(options as any).then(success as any, (e) => error?.(e));
  },

  watchPosition(success: Success, error?: Failure, options?: PositionOptions): number {
    if (!isNative()) {
      return navigator.geolocation.watchPosition(success, error, options);
    }
    const id = nextWatchId++;
    nativeWatches.set(
      id,
      Geolocation.watchPosition((options ?? {}) as any, (position, err) => {
        if (err) error?.(err);
        else if (position) success(position as any);
      })
    );
    return id;
  },

  clearWatch(id: number) {
    if (!isNative()) {
      navigator.geolocation.clearWatch(id);
      return;
    }
    const pending = nativeWatches.get(id);
    nativeWatches.delete(id);
    pending?.then((callbackId) => Geolocation.clearWatch({ id: callbackId })).catch(() => {});
  },
};
